import type { LDReactClient } from '@launchdarkly/react-sdk';
import { dashCaseToCamelCase, type DashToCamelCase } from 'integrations/launchDarkly/impl/dashCaseToCamelCase';

export type FlagType = 'boolean' | 'string' | 'json' | 'number';
export type FlagNamesToObjectOutput<FLAG_NAME extends string, TYPE extends FlagType> = {
  [K in DashToCamelCase<FLAG_NAME>]: FlagValue<TYPE>;
};
type FlagValue<T extends FlagType> = T extends 'boolean'
  ? boolean
  : T extends 'string'
    ? string
    : T extends 'number'
      ? number
      : object;

export function flagNamesToObject<FLAG_NAME extends string, TYPE extends FlagType>(
  ldClient: LDReactClient,
  flagNames: FLAG_NAME[],
  type: TYPE
): FlagNamesToObjectOutput<FLAG_NAME, TYPE> {
  return flagNames.reduce(
    (acc, flagName) => {
      let value: boolean | string | number | object;
      switch (type) {
        case 'boolean':
          value = ldClient.boolVariation(flagName, false);
          break;
        case 'string':
          value = ldClient.stringVariation(flagName, '');
          break;
        case 'number':
          value = ldClient.numberVariation(flagName, 0);
          break;
        case 'json':
          value = ldClient.jsonVariation(flagName, {}) as object;
          break;
        default:
          throw new Error(`Unsupported flag type: ${type}`);
      }
      acc[dashCaseToCamelCase(flagName)] = value as FlagValue<TYPE>;
      return acc;
    },
    {} as FlagNamesToObjectOutput<FLAG_NAME, TYPE>
  );
}
